// WHAT: Statistics component displaying timeline metrics overview
// WHY:  Provides quick overview of timeline statistics (totals, status counts, progress, timing)
// DOMAIN: Admin → Roadmap → Timeline
// DEPENDENCIES: Stack, Card, Badge components, Lucide icons
// RISKS:
//   - NOTE: Uses index as key in map - consider using unique identifier
//   - NOTE: averageProgress is rounded - may lose precision
//   - NOTE: Grid layout may not be responsive on very small screens

import {
  LucideTrendingUp,
  LucideTarget,
  LucideClock,
  LucideCheckCircle,
  LucideAlertTriangle,
} from "lucide-react";
import React from "react";

import { Stack, Card, Badge } from "@/components/ui";

/**
 * Props for TimelineStats component
 */
interface TimelineStatsProperties {
  /** Average progress across all items (0-100) */
  averageProgress: number;
  /** Number of blocked items */
  blockedItems: number;
  /** Number of completed items */
  completedItems: number;
  /** Number of in progress items */
  inProgressItems: number;
  /** Number of items on time */
  onTimeItems: number;
  /** Number of overdue items */
  overdueItems: number;
  /** Number of planned items */
  plannedItems: number;
  /** Total number of timeline items */
  totalItems: number;
}

/**
 * WHAT:
 *   Component that displays timeline statistics in a grid of metric cards,
 *   showing totals, status counts, average progress, and timing metrics.
 *
 * WHY:
 *   Provides quick visual overview of timeline health and progress metrics
 *   to help users understand the current state of their roadmap.
 *
 * PROPS:
 *   - totalItems: number — Total item count
 *   - completedItems: number — Completed status count
 *   - inProgressItems: number — In progress status count
 *   - plannedItems: number — Planned status count
 *   - blockedItems: number — Blocked status count
 *   - averageProgress: number — Average progress (0-100)
 *   - onTimeItems: number — On time items count
 *   - overdueItems: number — Overdue items count
 *
 * BEHAVIOR:
 *   - Renders 8 statistic cards in responsive grid (2/4/8 columns)
 *   - Each card shows icon, label badge, and value
 *   - Formats average progress as percentage with rounding
 *
 * RISKS:
 *   - NOTE: Index used as key - should use unique identifier if stats array changes
 *   - NOTE: averageProgress rounding may lose precision for small values
 *   - NOTE: Grid layout may cause wrapping issues on very small screens
 *
 * @param root0
 *
 * @param root0.totalItems
 *
 * @param root0.completedItems
 *
 * @param root0.inProgressItems
 *
 * @param root0.plannedItems
 *
 * @param root0.blockedItems
 *
 * @param root0.averageProgress
 *
 * @param root0.onTimeItems
 *
 * @param root0.overdueItems
 *
 * @returns {JSX.Element} Die gerenderte TimelineStats-Komponente mit Statistik-Karten
 *
 */
export const TimelineStats: React.FC<TimelineStatsProperties> = ({
  averageProgress,
  blockedItems,
  completedItems,
  inProgressItems,
  onTimeItems,
  overdueItems,
  plannedItems,
  totalItems,
}) => {
  const stats = [
    {
      icon: LucideTarget,
      label: "Total Items",
      value: totalItems,
      variant: "default" as const,
    },
    {
      icon: LucideCheckCircle,
      label: "Completed",
      value: completedItems,
      variant: "success" as const,
    },
    {
      icon: LucideClock,
      label: "In Progress",
      value: inProgressItems,
      variant: "primary" as const,
    },
    {
      icon: LucideTarget,
      label: "Planned",
      value: plannedItems,
      variant: "default" as const,
    },
    {
      icon: LucideAlertTriangle,
      label: "Blocked",
      value: blockedItems,
      variant: "destructive" as const,
    },
    {
      icon: LucideTrendingUp,
      label: "Avg Progress",
      value: `${String(Math.round(averageProgress))}%`,
      variant: "accent" as const,
    },
    {
      icon: LucideCheckCircle,
      label: "On Time",
      value: onTimeItems,
      variant: "success" as const,
    },
    {
      icon: LucideAlertTriangle,
      label: "Overdue",
      value: overdueItems,
      variant: "destructive" as const,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
      {stats.map((stat) => {
        const IconComponent = stat.icon;
        return (
          <Card className="text-center" key={stat.label} padding="md" variant="default">
            <Stack align="center" direction="col" gap={2}>
              <div className="flex items-center gap-2">
                <IconComponent className="w-5 h-5 text-slate-300" />
                <Badge size="sm" style="subtle" variant={stat.variant}>
                  {stat.label}
                </Badge>
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
            </Stack>
          </Card>
        );
      })}
    </div>
  );
};
